/**
 * API Client Factory
 * Creates and configures Axios instances with interceptors
 */

import axios, { AxiosInstance } from 'axios';
import { setupRequestInterceptor, setupResponseInterceptor } from './api-interceptors';

export class ApiClientFactory {
  private static instance: AxiosInstance | null = null;

  /**
   * Initialize API client
   */
  static initialize(baseURL: string): AxiosInstance {
    if (this.instance) {
      return this.instance;
    }

    this.instance = axios.create({
      baseURL,
      timeout: 30000,
      // Send the httpOnly refresh-token cookie on auth endpoints (harmless elsewhere).
      withCredentials: true,
    });

    // Setup interceptors
    setupRequestInterceptor(this.instance);
    setupResponseInterceptor(this.instance, baseURL);

    return this.instance;
  }

  /**
   * Get existing instance or create new one
   */
  static getInstance(baseURL?: string): AxiosInstance {
    if (!this.instance) {
      if (!baseURL) {
        throw new Error('API client not initialized. Call initialize() first.');
      }
      return this.initialize(baseURL);
    }
    return this.instance;
  }

  /**
   * Reset instance (for testing)
   */
  static reset(): void {
    this.instance = null;
  }
}

/**
 * Get or create API client
 */
export const getApiClient = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  return ApiClientFactory.getInstance(baseURL);
};
